/**
 * Timezone Utility
 * Handles date/time formatting according to user's selected timezone
 * All dates from backend are in UTC, this utility converts them for display
 */

import { DEFAULT_TIMEZONE } from './timezone.constants';

/**
 * Gets user's timezone preference from localStorage or defaults to UTC
 * @returns {string} IANA timezone string
 */
export function getUserTimezone() {
    try {
        const stored = localStorage.getItem('tb_userTimezone');
        return stored || DEFAULT_TIMEZONE;
    } catch (error) {
        console.error('Error getting user timezone:', error);
        return DEFAULT_TIMEZONE;
    }
}

/**
 * Hook-like function that can be called to get current timezone
 * This will read fresh from localStorage each time
 * For React components, use the useTimezone hook instead
 */
export function getCurrentTimezone() {
    return getUserTimezone();
}

/**
 * Sets user's timezone preference in localStorage
 * @param {string} timezone - IANA timezone string
 */
export function setUserTimezone(timezone) {
    try {
        localStorage.setItem('tb_userTimezone', timezone || DEFAULT_TIMEZONE);
        // Dispatch event for other components to listen
        window.dispatchEvent(new CustomEvent('tb:timezoneChanged', { 
            detail: { timezone: timezone || DEFAULT_TIMEZONE } 
        }));
    } catch (error) {
        console.error('Error setting user timezone:', error);
    }
}

/**
 * Gets timezone abbreviation dynamically (handles DST automatically)
 * @param {string} timezone - IANA timezone string
 * @param {Date} date - Date to get abbreviation for (defaults to now)
 * @returns {string} Timezone abbreviation (e.g., IST, EST, EDT, CST, CDT, GMT, BST, CET, CEST, GST, UTC)
 */
export function getTimezoneAbbreviation(timezone, date = new Date()) {
    try {
        // Direct mapping for timezones that browsers often return as offset format
        // or don't have standard abbreviations
        const directMappings = {
            'UTC': 'UTC',
            'Asia/Kolkata': 'IST', // India Standard Time - always IST (no DST)
            'Asia/Dubai': 'GST' // Gulf Standard Time - always GST (no DST)
        };
        
        if (directMappings[timezone]) {
            return directMappings[timezone];
        }
        
        // For timezones with DST, use Intl.DateTimeFormat to get the actual abbreviation
        // This automatically handles DST (e.g., EST vs EDT, CST vs CDT, GMT vs BST)
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'short'
        });
        
        const parts = formatter.formatToParts(date);
        const timeZoneName = parts.find(part => part.type === 'timeZoneName');
        
        if (timeZoneName && timeZoneName.value) {
            const abbrev = timeZoneName.value;
            
            // Some browsers return offset-based names (like "GMT+5:30") instead of abbreviations
            // If we get an offset format, use our DST-aware mapping
            if (abbrev.startsWith('GMT+') || abbrev.startsWith('GMT-') || abbrev.startsWith('UTC+') || abbrev.startsWith('UTC-')) {
                // Calculate offset to determine DST
                const dateStr = date.toLocaleString('en-US', { timeZone: timezone });
                const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' });
                const dateInTz = new Date(dateStr);
                const dateInUtc = new Date(utcStr);
                const offsetHours = (dateInTz.getTime() - dateInUtc.getTime()) / (1000 * 60 * 60);
                
                // Map based on timezone and offset
                if (timezone === 'America/New_York' || timezone === 'America/Toronto') {
                    return offsetHours === -5 ? 'EST' : 'EDT'; // EST: UTC-5, EDT: UTC-4
                } else if (timezone === 'America/Chicago') {
                    return offsetHours === -6 ? 'CST' : 'CDT'; // CST: UTC-6, CDT: UTC-5
                } else if (timezone === 'Europe/London') {
                    return offsetHours === 0 ? 'GMT' : 'BST'; // GMT: UTC+0, BST: UTC+1
                } else if (timezone === 'Europe/Berlin') {
                    return offsetHours === 1 ? 'CET' : 'CEST'; // CET: UTC+1, CEST: UTC+2
                }
                
                // Unknown offset format, return as-is
                return abbrev;
            }
            
            return abbrev;
        }
        
        // Final fallback mapping
        const fallbackMap = {
            'UTC': 'UTC',
            'Asia/Kolkata': 'IST',
            'America/New_York': 'EST',
            'America/Toronto': 'EST',
            'America/Chicago': 'CST',
            'Europe/London': 'GMT',
            'Europe/Berlin': 'CET',
            'Asia/Dubai': 'GST'
        };
        
        return fallbackMap[timezone] || 'UTC';
    } catch (error) {
        console.error('Error getting timezone abbreviation:', error);
        return 'UTC';
    }
}

/**
 * Formats a UTC timestamp for display in user's timezone
 * @param {Date|string} utcDate - UTC date/timestamp from backend
 * @param {object} options - Formatting options (optional)
 * @param {boolean} includeTimezone - Whether to include timezone abbreviation (default: true)
 * @returns {string} Formatted date string
 */
export function formatDateForDisplay(utcDate, options = {}, includeTimezone = true) {
    if (!utcDate) return 'N/A';
    
    try {
        let date;
        
        // Handle different input types
        if (utcDate instanceof Date) {
            date = utcDate;
        } else if (typeof utcDate === 'string') {
            // If it's already an ISO string with Z, use it directly
            if (utcDate.endsWith('Z')) {
                date = new Date(utcDate);
            } 
            // If it's a PostgreSQL timestamp format (space instead of T), treat as UTC
            else if (utcDate.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/)) {
                // Replace space with T and append Z to indicate UTC
                const utcString = utcDate.replace(' ', 'T') + (utcDate.includes('.') ? '' : '.000') + 'Z';
                date = new Date(utcString);
            }
            // If it's ISO format without Z, append Z to indicate UTC
            else if (utcDate.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/) && !utcDate.endsWith('Z')) {
                date = new Date(utcDate + (utcDate.includes('.') ? '' : '.000') + 'Z');
            }
            // Otherwise, try parsing as-is
            else {
                date = new Date(utcDate);
            }
        } else {
            date = new Date(utcDate);
        }
        
        // Validate date
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        
        const userTimezone = getUserTimezone();
        
        const defaultOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: userTimezone
        };
        
        const formatOptions = { ...defaultOptions, ...options };
        
        const formattedDate = new Intl.DateTimeFormat('en-US', formatOptions).format(date);
        
        // Add timezone abbreviation if requested
        if (includeTimezone) {
            const tzAbbr = getTimezoneAbbreviation(userTimezone, date);
            return `${formattedDate} ${tzAbbr}`;
        }
        
        return formattedDate;
    } catch (error) {
        console.error('Error formatting date:', error);
        // Fallback to basic formatting
        try {
            // Try to ensure UTC interpretation
            let dateStr = utcDate;
            if (typeof utcDate === 'string' && !utcDate.endsWith('Z') && utcDate.match(/^\d{4}-\d{2}-\d{2}/)) {
                dateStr = utcDate.replace(' ', 'T') + (utcDate.includes('.') ? '' : '.000') + 'Z';
            }
            const formatted = new Date(dateStr).toLocaleString('en-US', {
                timeZone: getUserTimezone(),
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            });
            if (includeTimezone) {
                const userTimezone = getUserTimezone();
                const tzAbbr = getTimezoneAbbreviation(userTimezone);
                return `${formatted} ${tzAbbr}`;
            }
            return formatted;
        } catch (fallbackError) {
            return 'Invalid Date';
        }
    }
}

/**
 * Formats a UTC timestamp for display (date only)
 * @param {Date|string} utcDate - UTC date/timestamp from backend
 * @param {boolean} includeTimezone - Whether to include timezone abbreviation (default: false for date only)
 * @returns {string} Formatted date string
 */
export function formatDateOnly(utcDate, includeTimezone = false) {
    return formatDateForDisplay(utcDate, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }, includeTimezone);
}

/**
 * Formats a UTC timestamp for display (time only)
 * @param {Date|string} utcDate - UTC date/timestamp from backend
 * @param {boolean} includeTimezone - Whether to include timezone abbreviation (default: true for time)
 * @returns {string} Formatted time string
 */
export function formatTimeOnly(utcDate, includeTimezone = true) {
    return formatDateForDisplay(utcDate, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    }, includeTimezone);
}

/**
 * Formats timestamp for display (replaces old formatTimestamp function)
 * This is a drop-in replacement for the existing formatTimestamp utility
 * @param {string|Date} timestamp - The timestamp to format
 * @param {boolean} includeTimezone - Whether to include timezone abbreviation (default: true)
 * @returns {string} Formatted timestamp string
 */
export function formatTimestamp(timestamp, includeTimezone = true) {
    return formatDateForDisplay(timestamp, {}, includeTimezone);
}

